import { clamp } from '../core/math';
import { P } from '../world/player';
import { enemies } from '../entities/enemies';

/* ============================================================
   衝擊力與踉蹌 —— 參考《ARMORED CORE VI》的 ACS 負荷

   熱量系統（M3）已退場，理由是「作業感」：儀表在玩家身上、會自己漲、漲滿要罰，
   於是它從可選的資源變成被迫處理的計時器，而唯一的決策「什麼時候倒」答案永遠一樣。

   AC6 的解法是**把累積的儀表放在敵人身上**：

     熱量（已退場）              踉蹌（本系統）
     儀表在玩家身上              儀表在敵人身上
     不理它 → 過熱、損失資源     不理它 → 沒有後果
     填它 = 開火 + 被動累積      填它 = 攻擊，本來就要做的事
     心理：義務                  心理：機會

   核心迴圈：選定目標 → 集中火力 → 填滿衝擊值 → 敵人踉蹌並大幅增傷 → 傾瀉輸出。
   衝擊值會隨時間衰減，所以「分散打」填不滿 —— 這就是集中火力的理由，
   也是手動鎖定（sim/lockon.ts）存在的原因。
   ============================================================ */

export var IMPACT = {
  decayDelay: 0.6,      // 多久沒被打到才開始衰減
  decayRate: 0.4,       // 每秒衰減「最大值」的比例
  staggerTime: 1.6,     // 一般敵人踉蹌秒數
  staggerTimeBoss: 2.6,
  staggerMul: 2.5,      // 一般敵人踉蹌期間的增傷倍率
  recoverCd: 1.2,       // 踉蹌結束後多久才能重新累積，避免無限鎖定

  /* Boss 專用的四個數字，目的是讓踉蹌從「順便發生的加成」變成**必須依賴的機制**。

     原本的問題：Boss 未踉蹌時照樣吃滿傷害，所以不打踉蹌也能在 21 秒內解決，
     踉蹌只是把時間縮到 15 秒 —— 可有可無，玩家感覺不到自己在用這個系統。

     現在：未踉蹌時只吃 45% 傷害，踉蹌時吃 3.5 倍，踉蹌窗口貢獻整場約八成輸出。
     實測正常打踉蹌 19.2 秒、完全不踉蹌 49.7 秒，差 2.6 倍。
     衰減也更狠（延遲 0.35 秒、每秒 80%），停火閃避就會掉進度 ——
     這才是「要不要繼續壓上去」的取捨。 */
  bossArmor: 0.45,       // 未踉蹌時的傷害倍率
  staggerMulBoss: 3.5,   // 踉蹌期間的增傷倍率
  bossDecayDelay: 0.35,
  bossDecayRate: 0.8,
};

/** 這一擊累積多少衝擊。不同武裝的衝擊效率不同，近戰最高。

   **衝擊上限必須對照「打死要幾發」來設，否則敵人會在踉蹌之前先死。**
   2026-08-01 第一版就犯了這個錯：一般敵人的上限全都高於牠們死前能累積的量，
   結果只有 Boss 打得出踉蹌，玩家在前 55 秒完全看不到這個系統。
   現在的基準是「打到約一半血量時踉蹌」，讓 ×2.5 的窗口覆蓋後半段。 */
export var IMPACT_MUL = {
  vulcan: 0.5,      // 機炮是磨血武器，衝擊效率低
  blade: 1.2,       // 近戰衝擊最高，貼身的回報
  /* 連段第三段（下劈）。1.2 × 1.6 —— **這是這一輪唯一動到的強度**。
     踉蹌是全遊戲唯一「集中火力有額外回報」的系統，而連段就是集中火力；
     把兩者接起來，近戰的身份才成立（`.claude/notes/lancer2-arts.md` §1）。 */
  bladeHeavy: 1.92,
  missile: 0.8,   // 一次齊射 4 發 × 34 傷，係數太高會讓飛彈變成唯一解
  dashSlash: 1.1,
  beam: 0.4,        // 本來就是多段命中，係數壓低避免瞬間填滿
  vortex: 0.8,
  prop: 1.0,        // 場地構件等其他來源
};

export function staggered(e) { return e.staggerT > 0; }

/** 傷害倍率。Boss 未踉蹌時有裝甲減傷，踉蹌時大幅增傷 —— 見 IMPACT 的註解。 */
export function damageMul(e) {
  /* 精英吃 Boss 那一組倍率（未踉蹌時有裝甲、踉蹌時大幅增傷）：
     它的定位就是「小一號的 Boss」，踉蹌對它要有同樣的意義。 */
  var heavy = e.T.boss || e.elite;
  /* 天藍・護盾（SCHILD / BLASE）給友軍的減傷。乘在這裡而不是各武裝，
     所以任何新武裝都自動吃到 —— 而且**踉蹌時一樣有效**：
     護盾的正解是「先打施放者」，不是「等它踉蹌再打」。 */
  var arm = e.buffArm > 1 ? 1 / e.buffArm : 1;
  /* 踉蹌增傷吃 `stagDmg`（被動「過載鎖定」）。**只乘在踉蹌分支上** ——
     乘在整個 damageMul 上的話它就退化成普通的傷害加成，
     那條軸線「必須先打出踉蹌才有回報」的性格就沒了。 */
  if (e.staggerT > 0) return arm * (heavy ? IMPACT.staggerMulBoss : IMPACT.staggerMul)
    * ((P.s && P.s.stagDmg) || 1);
  return arm * (heavy ? IMPACT.bossArmor : 1);
}

/* 衝擊上限。**讀每隻的 `impCap` 而不是型別上的 `T.imp`** ——
   精英是雜魚升格來的（共用同一個 ETYPE），改型別的話整個池子跟著變。
   沒設 `impCap` 就退回型別值，所以一般敵人的行為完全不變。 */
export function impCap(e) {
  if (e.impCap !== undefined) return e.impCap;
  /* **踉蹌只留給精英與 Boss。** 兩個理由，一個玩法一個技術：

     玩法：同屏 300 隻的時候，雜魚踉蹌的白環與頓幀是純噪音 ——
     「這東西很硬」的訊號被稀釋成背景（lessons：雜魚一分鐘被觸發 17 次）。

     技術：雜魚改成實例化渲染之後，材質是整個型別共用的，
     沒辦法讓「這一隻」發光。踉蹌看不出來的話它就不存在。 */
  return (e.T.boss || e.elite) ? e.T.imp : 0;
}

export function addImpact(e, amount) {
  /* `P.s.imp` 是衝擊軸的乘數（被動「動能穿甲」）。乘在**進入點**而不是各呼叫端，
     所以任何新武裝都自動吃到這條軸，不用逐一接線。 */
  amount *= (P.s && P.s.imp) || 1;
  /* imp = 0 代表這種敵人不會踉蹌。雜魚三發就死，踉蹌對牠毫無意義，
     只會製造每分鐘十幾次的白環與頓幀噪音 —— 踉蹌要留給「這東西很硬」的場合。 */
  var max = impCap(e);
  if (!max) return false;
  if (e.staggerT > 0 || e.staggerCd > 0) return false;   // 踉蹌中與恢復期不累積
  e.impact = clamp(e.impact + amount, 0, max);
  e.impactIdle = 0;
  e.dirty = true;   // 讓渲染層更新發光強度
  if (e.impact >= max) {
    e.impact = max;
    e.staggerT = ((e.T.boss || e.elite) ? IMPACT.staggerTimeBoss : IMPACT.staggerTime)
      * ((P.s && P.s.stagT) || 1);
    return true;      // 呼叫端負責演出（音效、環、頓幀）
  }
  return false;
}

/** 鎖定共鳴：把死掉的目標身上的衝擊進度，按比例搬給場上最近的另一隻。

    沒有這條規則時，「專心打一隻」的代價是它死掉那一刻進度歸零 ——
    而那正是衝擊 build 最痛的地方（尤其精英成群出現時）。 */
export function relayImpact(dead, ratio) {
  var cap = impCap(dead);
  if (!cap || dead.impact <= 0) return;
  var carry = dead.impact * ratio, best = null, bd = 1e9;
  for (var i = 0; i < enemies.length; i++) {
    var e = enemies[i];
    if (e === dead || !impCap(e) || e.staggerT > 0 || e.staggerCd > 0) continue;
    var d = Math.hypot(e.pos.x - dead.pos.x, e.pos.z - dead.pos.z);
    if (d < bd) { bd = d; best = e; }
  }
  if (!best || bd > 26) return;
  addImpact(best, carry);
}

/** 每個敵人每步呼叫一次。回傳 true 表示這一步剛結束踉蹌。 */
export function updateImpact(e, dt) {
  if (e.staggerT > 0) {
    e.staggerT -= dt;
    if (e.staggerT <= 0) {
      e.staggerT = 0; e.impact = 0; e.staggerCd = IMPACT.recoverCd;
      return true;
    }
    return false;
  }
  if (e.staggerCd > 0) { e.staggerCd -= dt; return false; }

  e.impactIdle += dt;
  var delay = (e.T.boss || e.elite) ? IMPACT.bossDecayDelay : IMPACT.decayDelay;
  var rate = (e.T.boss || e.elite) ? IMPACT.bossDecayRate : IMPACT.decayRate;
  if (e.impactIdle > delay && e.impact > 0) {
    e.impact = Math.max(0, e.impact - impCap(e) * rate * dt);
  }
  return false;
}

/** 0–1，給 HUD 與敵人的發光程度用。 */
export function impactRatio(e) {
  var max = impCap(e);
  return max > 0 ? e.impact / max : 0;
}
